import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, RotateCcw, Trophy, CheckCircle2, Download, Share2, Copy, Check, Users, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SignUpPrompt } from "../components/SignUpPrompt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: 1, category: "Environment",
    text: "The government should take urgent, ambitious action on climate change",
    stances: { alp: 2, liberal: 0, greens: 2, nationals: -1, "one-nation": -2, lnp: 0, teal: 2, uap: -2, jln: 0, clp: -1, "wa-nationals": -1, "sa-best": 1 },
  },
  {
    id: 2, category: "Immigration",
    text: "Immigration to Australia should be significantly reduced",
    stances: { alp: -1, liberal: 1, greens: -2, nationals: 0, "one-nation": 2, lnp: 1, teal: -1, uap: 1, jln: 0, clp: 1, "wa-nationals": 0, "sa-best": 0 },
  },
  {
    id: 3, category: "Healthcare",
    text: "Medicare and public healthcare should receive significantly more government funding",
    stances: { alp: 2, liberal: 0, greens: 2, nationals: 1, "one-nation": 1, lnp: 1, teal: 1, uap: 0, jln: 2, clp: 0, "wa-nationals": 1, "sa-best": 1 },
  },
  {
    id: 4, category: "Education",
    text: "University education should be free or heavily subsidised for all Australians",
    stances: { alp: 1, liberal: -1, greens: 2, nationals: -1, "one-nation": -1, lnp: -1, teal: 1, uap: -1, jln: 1, clp: -1, "wa-nationals": -1, "sa-best": 0 },
  },
  {
    id: 5, category: "Energy",
    text: "Australia should build nuclear power plants for energy generation",
    stances: { alp: -2, liberal: 2, greens: -2, nationals: 2, "one-nation": 2, lnp: 2, teal: -1, uap: 2, jln: 1, clp: 2, "wa-nationals": 2, "sa-best": 0 },
  },
  {
    id: 6, category: "Workers",
    text: "Workers deserve stronger union rights, job protections, and higher minimum wages",
    stances: { alp: 2, liberal: -1, greens: 2, nationals: 0, "one-nation": 1, lnp: -1, teal: 0, uap: -1, jln: 2, clp: -1, "wa-nationals": 0, "sa-best": 1 },
  },
  {
    id: 7, category: "Housing",
    text: "The government should build large-scale public housing to solve the affordability crisis",
    stances: { alp: 2, liberal: -1, greens: 2, nationals: 1, "one-nation": 1, lnp: 0, teal: 1, uap: 0, jln: 2, clp: 0, "wa-nationals": 1, "sa-best": 1 },
  },
  {
    id: 8, category: "Economy",
    text: "Taxes should be lower, even if it means reducing some government services",
    stances: { alp: -1, liberal: 2, greens: -2, nationals: 0, "one-nation": 1, lnp: 2, teal: -1, uap: 2, jln: -1, clp: 1, "wa-nationals": 0, "sa-best": -1 },
  },
  {
    id: 9, category: "Regional",
    text: "The government should significantly increase support for farmers and regional communities",
    stances: { alp: 1, liberal: 1, greens: 0, nationals: 2, "one-nation": 2, lnp: 2, teal: 0, uap: 1, jln: 1, clp: 2, "wa-nationals": 2, "sa-best": 1 },
  },
  {
    id: 10, category: "Borders",
    text: "Australia needs stricter border protection and tougher asylum seeker policies",
    stances: { alp: 0, liberal: 2, greens: -2, nationals: 1, "one-nation": 2, lnp: 2, teal: -1, uap: 2, jln: 0, clp: 2, "wa-nationals": 1, "sa-best": 0 },
  },
];

const PARTY_INFO = {
  alp: { name: "Australian Labor Party", abbreviation: "ALP", color: "#CC2529" },
  liberal: { name: "Liberal Party of Australia", abbreviation: "LIB", color: "#1B4F9E" },
  greens: { name: "Australian Greens", abbreviation: "GRN", color: "#10B981" },
  nationals: { name: "The Nationals", abbreviation: "NAT", color: "#155E3B" },
  "one-nation": { name: "One Nation", abbreviation: "PHON", color: "#D97706" },
  lnp: { name: "Liberal National Party", abbreviation: "LNP", color: "#2563EB" },
  teal: { name: "Teal Independents", abbreviation: "TEAL", color: "#0D9488" },
  uap: { name: "United Australia Party", abbreviation: "UAP", color: "#F59E0B" },
  jln: { name: "Jacqui Lambie Network", abbreviation: "JLN", color: "#7C3AED" },
  clp: { name: "Country Liberal Party", abbreviation: "CLP", color: "#DC2626" },
  "wa-nationals": { name: "Nationals WA", abbreviation: "NATWA", color: "#065F46" },
  "sa-best": { name: "SA-Best", abbreviation: "SAB", color: "#9333EA" },
};

const ANSWER_OPTIONS = [
  { value: 1, label: "Strongly Disagree", bg: "#C04E3F" },
  { value: 2, label: "Disagree", bg: "#D97706" },
  { value: 3, label: "Neutral", bg: "#6B7280" },
  { value: 4, label: "Agree", bg: "#2E5948" },
  { value: 5, label: "Strongly Agree", bg: "#10B981" },
];

function calculateResults(allAnswers) {
  const scores = {};
  Object.keys(PARTY_INFO).forEach((id) => { scores[id] = 0; });
  QUESTIONS.forEach((q, idx) => {
    if (allAnswers[idx] === undefined) return;
    const userScore = allAnswers[idx] - 3;
    Object.keys(PARTY_INFO).forEach((partyId) => {
      const partyStance = q.stances[partyId] ?? 0;
      scores[partyId] += 4 - Math.abs(userScore - partyStance);
    });
  });
  const maxPossible = QUESTIONS.length * 4;
  return Object.entries(scores)
    .map(([id, score]) => ({
      id, ...PARTY_INFO[id], score,
      percentage: Math.round((score / maxPossible) * 100),
    }))
    .sort((a, b) => b.score - a.score);
}

// ── Shareable Card (captured by html-to-image) ────────────────────
function ShareableCard({ cardRef, top, results }) {
  return (
    <div
      ref={cardRef}
      style={{
        width: 600, height: 340,
        backgroundColor: "#0F172A",
        fontFamily: "Arial, Helvetica, sans-serif",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        padding: 36,
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      {/* Color accent bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 5, backgroundColor: top.color }} />
      {/* BG glow */}
      <div style={{
        position: "absolute", top: -80, right: -80,
        width: 320, height: 320, borderRadius: "50%",
        backgroundColor: top.color, opacity: 0.12,
      }} />
      {/* Label */}
      <div style={{ color: "#FFC82E", fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 16 }}>
        AusVote · Party Match Quiz
      </div>
      {/* Main row */}
      <div style={{ display: "flex", alignItems: "center", gap: 28, flex: 1 }}>
        {/* Party badge */}
        <div style={{
          width: 110, height: 110, backgroundColor: top.color,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "3px solid rgba(255,255,255,0.15)", flexShrink: 0,
        }}>
          <span style={{ color: "#fff", fontSize: 28, fontWeight: 900 }}>{top.abbreviation}</span>
        </div>
        <div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginBottom: 2 }}>My Best Match</div>
          <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, lineHeight: 1.2, marginBottom: 6 }}>{top.name}</div>
          <div style={{ color: top.color, fontSize: 54, fontWeight: 900, lineHeight: 1 }}>{top.percentage}%</div>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginTop: 4 }}>match with my values</div>
        </div>
      </div>
      {/* Mini rankings */}
      <div style={{ display: "flex", gap: 6, marginTop: 18 }}>
        {results.slice(1, 5).map((r, i) => (
          <div key={r.id} style={{
            flex: 1, backgroundColor: "rgba(255,255,255,0.05)",
            borderLeft: `3px solid ${r.color}`, padding: "5px 7px",
          }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 10, fontWeight: 700 }}>#{i + 2} {r.abbreviation}</div>
            <div style={{ color: r.color, fontSize: 13, fontWeight: 800 }}>{r.percentage}%</div>
          </div>
        ))}
      </div>
      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14 }}>
        <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>Find your match at AusVote</div>
        <div style={{ color: "#FFC82E", fontSize: 15, fontWeight: 900 }}>AusVote.</div>
      </div>
    </div>
  );
}

export default function QuizPage() {
  const navigate = useNavigate();
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null); // highlight before committing

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const challengeParty = params.get("challenge");
    const challengeScore = params.get("score");
    if (challengeParty && challengeScore && PARTY_INFO[challengeParty]) {
      setChallengeInfo({ ...PARTY_INFO[challengeParty], score: parseInt(challengeScore, 10) });
    }
  }, []);

  const commitAnswer = () => {
    if (selectedAnswer === null) return;
    const newAnswers = { ...answers, [currentQ]: selectedAnswer };
    setAnswers(newAnswers);
    setSelectedAnswer(null);
    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setResults(calculateResults(newAnswers));
    }
  };

  const reset = () => {
    setCurrentQ(0);
    setAnswers({});
    setResults(null);
    setSelectedAnswer(null);
    window.history.replaceState({}, "", "/quiz");
    setChallengeInfo(null);
  };

  const progress = (Object.keys(answers).length / QUESTIONS.length) * 100;

  if (results) {
    return <QuizResults results={results} onReset={reset} onNavigate={navigate} challengeInfo={challengeInfo} />;
  }

  const q = QUESTIONS[currentQ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F7F2" }} data-testid="quiz-page">
      {/* Challenge banner */}
      {challengeInfo && (
        <div
          className="border-b-2 border-black py-3 px-4"
          style={{ backgroundColor: challengeInfo.color }}
          data-testid="challenge-banner"
        >
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <Zap className="w-4 h-4 text-white flex-shrink-0" />
            <p className="text-sm font-bold text-white">
              Challenge: Your friend matched <strong>{challengeInfo.name}</strong> at{" "}
              <strong>{challengeInfo.score}%</strong>. Can you beat them? Take the quiz below!
            </p>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="quiz-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Party Match Quiz
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Which Party Matches You?
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            Answer 10 questions on key issues and discover which Australian political party best matches your values.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Progress */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-[#475569]">Question {currentQ + 1} of {QUESTIONS.length}</span>
          <span className="text-sm font-bold" style={{ color: "#2E5948" }}>{Math.round(progress)}% complete</span>
        </div>
        <div className="h-3 bg-gray-200 border-2 border-black mb-8">
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: "#2E5948" }} />
        </div>

        {/* Question Card */}
        <div
          className="bg-white border-2 border-black p-8 mb-4"
          style={{ boxShadow: "4px 4px 0px 0px #000000" }}
          data-testid={`question-${q.id}`}
        >
          <span
            className="inline-block px-2 py-1 text-xs font-bold border border-black mb-4"
            style={{ backgroundColor: "#2E594820", color: "#2E5948" }}
          >
            {q.category}
          </span>
          <h2 className="text-xl font-black mb-8 leading-snug" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
            "{q.text}"
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            {[...ANSWER_OPTIONS].reverse().map((opt) => {
              const isSelected = selectedAnswer === opt.value;
              const isPrevAnswer = answers[currentQ] === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`answer-${opt.value}`}
                  onClick={() => setSelectedAnswer(isSelected ? null : opt.value)}
                  className="py-4 px-2 border-2 border-black font-bold text-sm hover:-translate-y-1 transition-transform flex flex-col items-center gap-1.5"
                  style={{
                    backgroundColor: isSelected ? opt.bg : isPrevAnswer ? `${opt.bg}33` : "#fff",
                    color: isSelected ? "#fff" : "#0F172A",
                    boxShadow: isSelected ? `3px 3px 0 #000` : "2px 2px 0px 0px #000",
                    transform: isSelected ? "translateY(-4px)" : undefined,
                  }}
                >
                  <span className="text-xl font-black">{opt.value}</span>
                  <span className="text-xs font-semibold text-center leading-tight">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {/* Next button — only visible once an answer is selected */}
          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-[#475569] font-medium">
              {selectedAnswer !== null
                ? `Selected: ${ANSWER_OPTIONS.find(o => o.value === selectedAnswer)?.label}`
                : "Select an answer above to continue"}
            </p>
            <button
              onClick={commitAnswer}
              disabled={selectedAnswer === null}
              data-testid="quiz-next-btn"
              className="flex items-center gap-2 px-5 py-2.5 font-black text-sm border-2 border-black transition-all hover:-translate-y-0.5 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{
                backgroundColor: selectedAnswer !== null ? "#2E5948" : "#e5e7eb",
                color: selectedAnswer !== null ? "#fff" : "#6b7280",
                boxShadow: selectedAnswer !== null ? "3px 3px 0 #000" : "none",
              }}
            >
              {currentQ === QUESTIONS.length - 1 ? "See Results" : "Next"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Nav — Previous only (Next is inside the question card) */}
        <div className="flex items-center">
          <button
            onClick={() => { if (currentQ > 0) { setCurrentQ(currentQ - 1); setSelectedAnswer(null); } }}
            disabled={currentQ === 0}
            data-testid="quiz-prev-btn"
            className="flex items-center gap-2 px-4 py-2 font-bold border-2 border-black disabled:opacity-40 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
        </div>
      </div>
    </div>
  );
}

function QuizResults({ results, onReset, onNavigate, challengeInfo }) {
  const { user } = useAuth();
  const top = results[0];
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [challengeCopied, setChallengeCopied] = useState(false);
  const [shareXCopied, setShareXCopied] = useState(false);
  const [quizStats, setQuizStats] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const submittedRef = useRef(false);

  // Beat challenge?
  const beatChallenge = challengeInfo && top.percentage > challengeInfo.score;
  const challengeMsg = challengeInfo
    ? beatChallenge
      ? `You beat your friend! You matched ${top.name} at ${top.percentage}% vs their ${challengeInfo.score}% for ${challengeInfo.name}.`
      : `Your friend matched ${challengeInfo.name} at ${challengeInfo.score}%. You matched ${top.name} at ${top.percentage}%.`
    : null;

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    axios.post(`${API}/quiz/submit`, { top_party_id: top.id, top_percentage: top.percentage }).catch(() => {});
    axios.get(`${API}/quiz/stats`).then((r) => setQuizStats(r.data)).catch(() => {});
    // Save to user dashboard if logged in
    if (user) {
      axios
        .post(`${API}/user/save-quiz-result`, {
          top_party_id: top.id,
          top_percentage: top.percentage,
          full_results: results.map((r) => ({ party_id: r.id, percentage: r.percentage })),
        }, { withCredentials: true })
        .catch(() => {});
    }
  }, [top.id, top.percentage, user, results]);

  const downloadCard = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#0F172A" });
      const link = document.createElement("a");
      link.download = `ausvote-${top.id}-match.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error("Card download failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  const copyChallenge = () => {
    const url = `${window.location.origin}/quiz?challenge=${top.id}&score=${top.percentage}`;
    navigator.clipboard.writeText(url).then(() => {
      setChallengeCopied(true);
      setTimeout(() => setChallengeCopied(false), 3000);
    });
  };

  const shareOnX = () => {
    const shareText = `I took the AusVote party quiz and my best match is the ${top.name} (${top.percentage}%)! Find your match:`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText + " " + window.location.origin + "/quiz")}`,
      "_blank"
    );
  };

  // Stats: find top party from stats
  const topStat = quizStats?.stats?.[0];
  const topStatParty = topStat ? PARTY_INFO[topStat.party_id] : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F7F2" }} data-testid="quiz-results">
      {/* Challenge result banner */}
      {challengeInfo && (
        <div
          className="border-b-2 border-black py-3 px-4"
          style={{ backgroundColor: beatChallenge ? "#10B981" : "#C04E3F" }}
          data-testid="challenge-result-banner"
        >
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            {beatChallenge
              ? <Trophy className="w-4 h-4 text-white flex-shrink-0" />
              : <Zap className="w-4 h-4 text-white flex-shrink-0" />}
            <p className="text-sm font-bold text-white">{challengeMsg}</p>
          </div>
        </div>
      )}

      {/* Sign-up prompt for non-logged-in users */}
      {!user && (
        <div className="max-w-3xl mx-auto px-4 pt-5">
          <SignUpPrompt trigger="quiz" />
        </div>
      )}

      {/* Results hero */}
      <div className="py-14 px-4 border-b-2 border-black" style={{ backgroundColor: top.color }}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <Trophy className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <p className="text-base font-bold opacity-80 mb-1">Your Best Match</p>
          <h1 className="text-4xl sm:text-5xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {top.name}
          </h1>
          <div className="text-6xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
            {top.percentage}%
          </div>
          <p className="mt-2 opacity-80">match with your values</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">

        {/* Share Card preview toggle */}
        <div
          className="bg-white border-2 border-black p-5 mb-6"
          style={{ boxShadow: "4px 4px 0 #000" }}
          data-testid="share-card-section"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
                Share Your Result
              </h3>
              <p className="text-xs text-[#6B7280] mt-0.5">Download a shareable card or challenge a friend</p>
            </div>
            <button
              onClick={() => setShowCard(!showCard)}
              className="text-xs px-3 py-1.5 border-2 border-black font-bold hover:bg-gray-100 transition-colors"
            >
              {showCard ? "Hide Card" : "Preview Card"}
            </button>
          </div>

          {showCard && (
            <div className="mb-4 overflow-x-auto pb-2" data-testid="share-card-preview">
              <ShareableCard cardRef={cardRef} top={top} results={results} />
            </div>
          )}
          {!showCard && (
            <div ref={cardRef} style={{ position: "absolute", left: -9999, top: -9999, opacity: 0, pointerEvents: "none" }}>
              <ShareableCard cardRef={null} top={top} results={results} />
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="download-card-btn"
              onClick={downloadCard}
              disabled={downloading}
              className="flex items-center gap-2 px-4 py-2.5 text-white font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
              style={{ backgroundColor: "#0F172A", boxShadow: "2px 2px 0 #000" }}
            >
              <Download className="w-4 h-4" />
              {downloading ? "Generating..." : "Download Card"}
            </button>
            <button
              data-testid="share-x-btn"
              onClick={shareOnX}
              className="flex items-center gap-2 px-4 py-2.5 text-white font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#1DA1F2", boxShadow: "2px 2px 0 #000" }}
            >
              <Share2 className="w-4 h-4" /> Share on X
            </button>
            <button
              data-testid="challenge-friend-btn"
              onClick={copyChallenge}
              className="flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{
                backgroundColor: challengeCopied ? "#10B981" : "#FFC82E",
                color: challengeCopied ? "#fff" : "#000",
                boxShadow: "2px 2px 0 #000",
              }}
            >
              {challengeCopied ? <Check className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
              {challengeCopied ? "Challenge link copied!" : "Challenge a Friend"}
            </button>
          </div>
        </div>

        {/* Stats box */}
        {quizStats && quizStats.total > 0 && (
          <div
            className="bg-white border-2 border-black p-5 mb-6"
            style={{ boxShadow: "4px 4px 0 #000" }}
            data-testid="quiz-stats-section"
          >
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4" style={{ color: "#2E5948" }} />
              <h3 className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
                Community Quiz Stats
              </h3>
              <span
                className="ml-auto text-xs font-bold px-2 py-0.5 border border-black"
                style={{ backgroundColor: "#FFC82E" }}
              >
                {quizStats.total.toLocaleString()} quizzes taken
              </span>
            </div>
            <p className="text-xs text-[#475569] mb-3">
              {topStatParty
                ? `Most common match: ${topStatParty.name} (${Math.round((quizStats.stats[0].count / quizStats.total) * 100)}% of users)`
                : "Loading community data..."}
            </p>
            <div className="space-y-1.5">
              {quizStats.stats.slice(0, 5).map((stat) => {
                const party = PARTY_INFO[stat.party_id];
                if (!party) return null;
                const pct = Math.round((stat.count / quizStats.total) * 100);
                return (
                  <div key={stat.party_id} className="flex items-center gap-2" data-testid={`stat-${stat.party_id}`}>
                    <div
                      className="w-10 h-5 flex items-center justify-center border border-black flex-shrink-0"
                      style={{ backgroundColor: party.color }}
                    >
                      <span className="text-white text-xs font-black">{party.abbreviation}</span>
                    </div>
                    <div className="flex-1 h-3 bg-gray-200 border border-black">
                      <div
                        className="h-full transition-all duration-700"
                        style={{ width: `${pct}%`, backgroundColor: party.color }}
                      />
                    </div>
                    <span className="text-xs font-bold w-8 text-right" style={{ color: party.color }}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Full Rankings */}
        <h2 className="text-2xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
          Your Full Rankings
        </h2>

        <div className="space-y-3 mb-8">
          {results.map((party, idx) => (
            <div
              key={party.id}
              data-testid={`result-party-${party.id}`}
              className="bg-white border-2 border-black p-4 flex items-center gap-4"
              style={{ boxShadow: idx === 0 ? "4px 4px 0px 0px #000" : "none" }}
            >
              <div className="text-xl font-black w-8 text-center flex-shrink-0" style={{ color: idx === 0 ? top.color : "#6B7280" }}>
                #{idx + 1}
              </div>
              <div
                className="w-12 h-12 flex items-center justify-center border-2 border-black flex-shrink-0"
                style={{ backgroundColor: party.color }}
              >
                <span className="text-white font-black text-xs">{party.abbreviation}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-sm text-[#0F172A] truncate">{party.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-2.5 bg-gray-200 border border-black">
                    <div className="h-full" style={{ width: `${party.percentage}%`, backgroundColor: party.color }} />
                  </div>
                  <span className="text-sm font-bold flex-shrink-0" style={{ color: party.color }}>{party.percentage}%</span>
                </div>
              </div>
              {idx === 0 && <CheckCircle2 className="w-6 h-6 flex-shrink-0" style={{ color: party.color }} />}
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="retake-quiz-btn"
            onClick={onReset}
            className="flex items-center gap-2 px-6 py-3 font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
            style={{ backgroundColor: "#FFC82E", boxShadow: "4px 4px 0px 0px #000" }}
          >
            <RotateCcw className="w-4 h-4" /> Retake Quiz
          </button>
          <button
            data-testid="browse-parties-from-quiz-btn"
            onClick={() => onNavigate("/")}
            className="flex items-center gap-2 px-6 py-3 text-white font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
            style={{ backgroundColor: "#2E5948", boxShadow: "4px 4px 0px 0px #000" }}
          >
            Browse Parties →
          </button>
          <button
            data-testid="compare-from-quiz-btn"
            onClick={() => onNavigate("/compare")}
            className="flex items-center gap-2 px-6 py-3 font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
            style={{ backgroundColor: "#0F172A", color: "#FFC82E", boxShadow: "4px 4px 0px 0px #000" }}
          >
            Compare Parties →
          </button>
        </div>

        <p className="text-xs text-[#475569] mt-6">
          Results are based on self-reported policy stances for civic education purposes only. Always research parties directly before voting.
        </p>
      </div>
    </div>
  );
}
